import { defineStore } from 'pinia'
import teamApi from '~/apis/teamApi'
import { type Team, type NewTeam } from '~/constants/orgTypes'
import debounce from 'lodash.debounce'

const debouncedSave = debounce(
  async (team: Team): Promise<void> => {
    await teamApi.updateTeam(team)
  },
  1000,
  { maxWait: 5000 },
)

export const useTeamStore = defineStore('team', {
  state: () => ({
    selected: {} as Team,
    list: [] as Team[],
  }),
  getters: {},
  actions: {
    async selectTeam(teamId: string) {
      const team = await teamApi.getTeam(teamId)
      this.$state.selected = team ?? {}
    },
    getTeam(teamId: string): Team | undefined {
      const team = this.$state.list.find((team) => team.id === teamId)
      return team
    },
    async getTeams(orgId: string) {
      const teams = await teamApi.getTeams(orgId)
      this.$state.list = teams ?? []
      return teams
    },
    async createTeam(team: NewTeam) {
      const newTeam = await teamApi.createTeam(team as Team)
      this.$state.list.push(newTeam)
      return newTeam
    },
    // flush=true performs the PATCH now and THROWS on failure — see orgStore.
    async save(flush?: boolean): Promise<void> {
      if (!this.$state.selected.id) return
      if (flush) {
        debouncedSave.cancel()
        await teamApi.updateTeam(this.$state.selected)
        return
      }
      debouncedSave(this.$state.selected)
    },
    updateTeamField<K extends keyof Team>(teamField: K, value: Team[K]) {
      this.$state.selected[teamField] = value
    },
    async addUserToTeam(teamId: string, userId: string): Promise<void> {
      await teamApi.addUserToTeam(teamId, userId)
      await this.selectTeam(teamId)
    },
    async removeUserFromTeam(teamId: string, userId: string): Promise<void> {
      await teamApi.removeUserFromTeam(teamId, userId)
      await this.selectTeam(teamId)
    },
    async deleteTeam(teamId: string): Promise<void> {
      await teamApi.deleteTeam(teamId)
      this.$state.list = this.$state.list.filter((team) => team.id !== teamId)
      if (this.$state.selected.id === teamId) {
        this.$state.selected = {} as Team
      }
    },
  },
})
